import React, { useState, useRef, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import Select from 'react-select';
import { FaAngleLeft, FaChevronDown } from 'react-icons/fa6'
import { FaUserFriends, FaUniversity, FaMobileAlt } from 'react-icons/fa';
import { RiUserReceivedLine } from "react-icons/ri";
import { toast } from 'react-toastify';
import ReactFlagsSelect from 'react-flags-select';
import enLocale from "i18n-iso-countries/langs/en.json";

import CardsProgressSteps from '../../../../components/ProgressStep/CardsProgressSteps'

import './sendMoney.css'
import { fetchSupportedCountryBanks, getAvailableTransferMethods } from '../../../../actions';
import { fetchRecentRecipients, fetchRecipientDetails, fetchWallets, formatLabel, verifyBankAccount } from '../../../../actions/payments';
import { getAuthenticatedUser } from '../../../../actions/auth';
import { CardReceive } from 'iconsax-react';
import { getFlagUrl, getCurrencyInfoByCurrencyCode as getCurrencyInfo, getCurrencyInfoByCountryCode as getCountryCurrencyInfo } from '../../../../helpers/currency';

const sendOptions = [
  {
    id: 'cardplot',
    title: 'Cardplot Account',
    description: 'Send money directly to another Cardplot user instantly.',
    icon: <FaUserFriends size={16} className="text-primary" />,
    accountNameLabel: 'Enter Cardplot username/email',
  },
  {
    id: 'bank',
    title: 'Bank',
    description: 'Send money to any local or international bank account.',
    icon: <FaUniversity size={16} className="text-success" />,
    accountNameLabel: 'Bank Account Name',
    accountNumberLabel: 'Bank Account Number',
  },
  {
    id: 'mobile_money',
    title: 'Mobile Money',
    description: 'Send money to a mobile money wallet',
    icon: <FaMobileAlt size={16} className="text-warning" />,
    accountNameLabel: 'Mobile Money Name',
    accountNumberLabel: 'Mobile Money Number',
  },
  {
    id: 'wallet',
    title: 'Wallet',
    description: 'Send money to a third-party wallet app',
    icon: <FaMobileAlt size={16} className="text-info" />,
    accountNameLabel: 'Wallet Account ID/Name',
    accountNumberLabel: 'Wallet Account Number',
  },

  {
    id: 'bank_card',
    title: 'Bank Card',
    description: 'Send money to a bank card',
    icon: <CardReceive size={16} className="text-info" />,
    accountNameLabel: 'Bank Card Holder Name',
    accountNumberLabel: 'Bank Card Number',
  },
];

const Step1SendMoney = ({ userDetails }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedTransferLocation = location.state?.selectedTransferLocation;  
  const converterSendingDetails = location.state?.converterDetails;
  const [converterCalDetails, setConverterCalDetails] = useState(converterSendingDetails || null);
  const [selectedOption, setSelectedOption] = useState(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef(null);
  const isLocalTransfer = selectedTransferLocation && selectedTransferLocation.slug === 'local';
  const defaultCountry = userDetails?.country_code?.toLowerCase() || "ng";  
  const excludeCountryCode = defaultCountry?.toUpperCase();
  const [selectedCountry, setSelectedCountry] = useState();
  const { allowedCountries, currencyOptions, supportedCountryDetails } = useSelector((state) => state.country);
  const allowedCountryCodes = allowedCountries.map((code) => code.toUpperCase());
  const [userWallets, setUserWallets] = useState([]);
  const userLocalWallet = userWallets && userWallets.find((wallet) => wallet.currency_code === userDetails?.default_wallet?.currency_code);
  const [bankErrorMessage, setBankErrorMessage] = useState(null);

  const [currencyToSend, setCurrencyToSend] = useState('CNY');

  const getCurrencyInfoByCurrencyCode = (code) => {
    return getCurrencyInfo(currencyOptions, code);
  };
  const getCurrencyInfoByCountryCode = (countryCode) => {
    let curr = getCountryCurrencyInfo(currencyOptions, countryCode);
    if (curr && curr.currencyCode) return curr;
    
    const supported = supportedCountryDetails?.find(c => c.iso2_code.toUpperCase() === countryCode);
    if (supported) {
      const code = supported.primary_currency_code === 'CHY' ? 'CNY' : supported.primary_currency_code;
      return {
        countryCode: supported.iso2_code.toUpperCase(),
        currencyCode: code,
        name: supported.name,
      };
    }
    return {};
  }; 
  const filteredCountryCodes = !isLocalTransfer 
    ? allowedCountryCodes.filter(code => {
      const currency = getCurrencyInfoByCountryCode(code);
      if(code !== excludeCountryCode && currency.countryCode == code) return code;
    }) 
    : allowedCountryCodes;

  const [fundingSource, setFundingSource] = useState('');
  const [recipientUsername, setRecipientUsername] = useState('');
  const [userRecipientInput, setUserRecipientInput] = useState('');
  const [inputtedAccountName, setInputtedAccountName] = useState('');
  const [accountNameFieldDisabled, setAccountNameFieldDisabled] = useState(false);
  const [accountName, setAccountName] = useState('');
  const [bankName, setBankName] = useState('');
  const [bank_uuid, setBank_uuid] = useState('');
  const [bankCode, setBankCode] = useState('');
  const [userSelectedBank, setUserSelectedBank] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [mobileMoney, setMobileMoney] = useState('');
  const [phone, setPhone] = useState('');
  const [transactionDescription, setTransactionDescription] = useState('');
  const [transferPurpose, setTransferPurpose] = useState(null);
  const [transferRecipientType, setTransferRecipientType] = useState(null);
  const [transferRecipientRelationship, setTransferRecipientRelationship] = useState(null);
  const [invoiceDocument, setInvoiceDocument] = useState('');
  const [invoiceDocumentFile, setInvoiceDocumentFile] = useState(null);
  const [invoiceDocumentName, setInvoiceDocumentName] = useState('');
  const [isEdited, setIsEdited] = useState(false);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [verifyAccountLoading, setVerifyAccountLoading] = useState(false);
  const [banks, setBanks] = useState([]);
  const recipientCache = useRef({});  
  const dispatch = useDispatch(); 
  const [availableMethods, setAvailableMethods] = useState([]);  
  const [nextPageInfo, setNextPageInfo] = useState();
  const [recentRecipients, setRecentRecipients] = useState([]);
  const [showRecentDropdown, setShowRecentDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRecentRef = useRef(null);
  const [previewValue, setPreviewValue] = useState("");
  // const [provider, setProvider] = useState("");

  const isPayloadEmpty = !selectedTransferLocation || Object.values(selectedTransferLocation).every(
    (value) => value === undefined || value === null || value === ''  
  );  

  const customLabels = Object.fromEntries(
    (supportedCountryDetails || [])
      .filter(option => allowedCountryCodes.includes(option.iso2_code.toUpperCase()))
      .map(option => {
          const code = option.primary_currency_code === 'CHY' ? 'CNY' : option.primary_currency_code;
          return [
            option.iso2_code.toUpperCase(),
            `${option.name} (${code})`
        ];
    })
  );

  console.log(customLabels);

  // useEffect(() => {
  //   const defaultCountry = isLocalTransfer ? userDetails?.country_code?.toLowerCase() : "";
  //   setSelectedCountry(defaultCountry);
  // }, []);

  const filteredRecipients = recentRecipients.filter((item) =>
    item.toLowerCase().includes(userRecipientInput.toLowerCase())
  );

  const handleRecentRecipientSelect = (value) => {
    setUserRecipientInput(value);
    setShowRecentDropdown(false);
    setHighlightedIndex(-1);
  };

  const fetchUserRecentRecipient = async () => {
    try {
      const response = await fetchRecentRecipients();
      // setRecentRecipients(response.data.recent_recipients);
      const emailList = response.data.recent_recipients
      .map((recipient) => recipient.email)
      .filter(Boolean); // removes null or undefined emails

      setRecentRecipients(emailList);
    } catch (error) {
      // console.error("Error fetching recent recipients:", error);
    }
  }

  useEffect(() => {
    fetchUserRecentRecipient();
  }, [showRecentDropdown]);

  const handleRecentKeyDown = (e) => {
    if (!showRecentDropdown || filteredRecipients.length === 0) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => {
          const nextIndex = prev < filteredRecipients.length - 1 ? prev + 1 : 0;
          setPreviewValue(filteredRecipients[nextIndex]); 
          return nextIndex;
        });
        break;

      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => {
          const nextIndex = prev > 0 ? prev - 1 : filteredRecipients.length - 1;
          setPreviewValue(filteredRecipients[nextIndex]); 
          return nextIndex;
        });
        break;

      case "Enter":
        if (highlightedIndex >= 0) {
          e.preventDefault();
          const selected = filteredRecipients[highlightedIndex];
          setUserRecipientInput(selected);
          setPreviewValue(""); // ✅ confirm selection
          setShowRecentDropdown(false);
          setHighlightedIndex(-1);
        }
        break;

      case "Escape":
        setShowRecentDropdown(false);
        setPreviewValue("");
        break;

      default:
        break;
    }
  };


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRecentRef.current && !inputRecentRef.current.contains(event.target)) {
        setShowRecentDropdown(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  
  useEffect(() => {
    const transferLocation = location.state?.selectedTransferLocation;
    if (transferLocation && transferLocation.slug === 'local') {
      setSelectedCountry(userDetails?.country_code);
      fetchAvailableTransferMethods(userDetails?.country_code);
    }
  }, [location.state?.selectedTransferLocation, userDetails]);

  useEffect(() => {
    if (isPayloadEmpty) {
      navigate("/user/send_money");
    }
  }, [isPayloadEmpty, navigate]);
  
  
  const fetchAvailableTransferMethods = async (countryCode) => { 
    setLoadingOptions(true);       
    try {
      const response = await getAvailableTransferMethods(countryCode);
      setAvailableMethods(response.data.options);
      return response.data;
    } catch (error) {
      // console.error("Error fetching transfer methods:", error);
      return [];
    } finally {
      setLoadingOptions(false);
    }
  };

  const handleCountrySelect = (countryCode) => {
    setSelectedCountry(countryCode);
    fetchAvailableTransferMethods(countryCode?.toLowerCase());   
    const receivingCurrency = countryCode === 'CN' 
      ? currencyToSend 
      : getCurrencyInfoByCountryCode(countryCode).currencyCode;
    if (converterCalDetails) {
      setConverterCalDetails({
        ...converterCalDetails,
        receiveCurrency: receivingCurrency,
      });
    } 
  };

  const handleCurrencyToSendSelect = (currency) => {
    if (currencyToSend === currency) return;
    setCurrencyToSend(currency);
    setBank_uuid('');
    setBankName('');
    setUserSelectedBank(null);
    setAccountNumber('');
    setInputtedAccountName('');
    setAccountNameFieldDisabled(false);

    if (converterCalDetails) {
      setConverterCalDetails({
        ...converterCalDetails,
        receiveCurrency: currency,
      });
    }
  };

  useEffect(() => {
    if (converterSendingDetails) {
      const sendCurrencyInfo = getCurrencyInfoByCurrencyCode(converterSendingDetails.receiveCurrency);
      const recipientCountry = sendCurrencyInfo.countryCode; 
      handleCountrySelect(recipientCountry);
      if (recipientCountry === 'CN' && (converterSendingDetails.receiveCurrency === 'USD' || converterSendingDetails.receiveCurrency === 'CNY')) {
        setCurrencyToSend(converterSendingDetails.receiveCurrency);
      }
    }
  }, []);
  

  const filteredTransferOptions = sendOptions.filter(
    (option) => availableMethods.includes(option.id)
  );


  const getRecipientCredentials = async (recipientInput) => {
    if (recipientCache.current[recipientInput]) {
      setRecipientUsername(recipientCache.current[recipientInput]);
      return { success: true, data: recipientCache.current[recipientInput] };
    }

    setLoading(true);
    try {
      const response = await fetchRecipientDetails(recipientInput);  
      const receiversAccDetails = response.data.users;      
      if (receiversAccDetails.length > 0) {
        const recipientName = receiversAccDetails[0]?.full_name || receiversAccDetails[0]?.business?.name || receiversAccDetails[0]?.bank_account[0]?.account_name;
        
        setRecipientUsername(recipientName); 
        
        return { success: true, data: receiversAccDetails };
      } else {
        toast.error('Recipient account not found', { toastId: "validation-toast3" });
        return { success: false };
      }
      
      
    } catch (error) {
      // console.error("Error fetching user:", error);
      toast.error(error?.response?.data?.message || 'Recipient account not found', { toastId: "validation-toast3" });
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const fetchAllWallets = async () => {
      setLoading(true);
      try {
          const response = await fetchWallets();
          // const data = await getAuthenticatedUser(dispatch);
          
          const wallets = response.data.wallets.wallets;
          

          // Enrich wallet data with currencyOptions
          const enrichedWallets = wallets.map((wallet) => {
              const match = currencyOptions.find(opt => opt.currencyCode === wallet.currency_code);
              
              return {
                ...wallet,
                currencyName: match?.currencyName,
                currencyId: match?.id,
                currencySymbol: match?.symbol,
                flag: match?.flagUrl,
              };
          });

          setUserWallets(enrichedWallets);
      } catch (error) {
          // console.error("Error fetching wallets:", error);
      } finally {
          setLoading(false);
      }
  };

  useEffect(() => {
    if (currencyOptions.length > 0) {
      fetchAllWallets();
    }
  }, [currencyOptions]);

  const handleWalletSelectChange = (e) => {
    const selectedId = e.target.value;
    const selectedOption = userWallets.find((opt) => opt.id === selectedId);
    setFundingSource(selectedOption);
    setIsEdited(true);
  };  

  useEffect(() => {
    if (isLocalTransfer && userLocalWallet) {
      setFundingSource(userLocalWallet);
      setIsEdited(true);
    }
  }, [userLocalWallet, isLocalTransfer]);
    

  const handleGoBack = () => {
    if (converterCalDetails?.isFromConverter) {
      navigate('/user/converter', { state: { backToCalDefault: converterCalDetails } });
    } else {
      navigate("/user/send_money");
    }
  }    

  const handleContinue = async (e) => {
    e.preventDefault();
    const transferTypeIsMomoOrBank = selectedOption?.id != 'cardplot';

    if (isPayloadEmpty) return navigate("/user/send_money");
  
    if (!selectedOption) {
      toast.error("Please select a transfer option.", { toastId: "validation-toast1" });
      return;
    }

    if (!fundingSource) {
      toast.error("Please select a funding source.", { toastId: "validation-toast2" });
      return;
    }

    if (transferTypeIsMomoOrBank && !fundingSource) {
      toast.error("Please select a funding source.", { toastId: "validation-toast2" });
      return;
    }

    // if (transferTypeIsMomoOrBank && selectedCountry) {
    //   const selected__Country = getCurrencyInfoByCountryCode(selectedCountry);
    //   const selectedCountryCurrencyCode = getCurrencyInfoByCountryCode(selectedCountry).currencyCode;
    //   const sourceWalletCurrencyCode = fundingSource?.currency_code;
      
    //   if (selectedCountryCurrencyCode !== sourceWalletCurrencyCode) {
    //     toast.error("The currency of the selected country does not match the currency of the source wallet.", { toastId: "validation-toast3" });
    //     toast.info(`${selected__Country?.name} wallet is needed.`, { toastId: "validation_toast3" });        
    //     return;
    //   }
    // }

    if (selectedOption?.id === 'cardplot' && !userRecipientInput?.trim()) {
      toast.error("Please enter recipient's username.", { toastId: "validation-toast4" });
      return;
    }

    if (transferTypeIsMomoOrBank && (!bankName || !accountNumber?.trim() || !inputtedAccountName?.trim())) {
      toast.error(`Please select the recipient's ${selectedOption?.id === 'bank' ? 'bank' : 'mobile money operator'} and enter account number/name.`, { toastId: "validation-toast5" });
      return;
    }

    // if (selectedOption?.id === 'bank' && accountNumber?.length !== 10 ) {
    //   toast.error("Account number must be 10 digits.", { toastId: "validation-toast5" });
    //   setInputtedAccountName('');
    //   return;
    // }

    // if (selectedOption?.id === 'mobile_money' && (!mobileMoney || !phone?.trim())) {
    //   toast.error("Please select mobile Money and enter phone number.", { toastId: "validation-toast6" });
    //   return;
    // }

    if (transferTypeIsMomoOrBank && selectedCountry === 'CN') {
      if (!transferPurpose) {
        toast.error("Please select a transfer purpose.", { toastId: "validation-toast-purpose" });
        return;
      }
      if (!invoiceDocument) {
        toast.error("Please upload an invoice document.", { toastId: "validation-toast-doc" });
        return;
      }
    }

    let finalRecipientName = recipientUsername;
    let cardplotAcctNum
    let recipient_id
    let receiversUserData
    if (selectedOption?.id === 'cardplot') {
      const result = await getRecipientCredentials(userRecipientInput);
      if (!result.success) return;
      finalRecipientName = result.data[0]?.full_name || result.data[0]?.business?.name || result.data[0]?.bank_account[0]?.account_name;
      cardplotAcctNum = result.data[0]?.bank_account[0]?.account_number;
      recipient_id = result.data[0]?.id;
      receiversUserData = result.data[0];
    }

    let finalAccountName = accountName;
    if (transferTypeIsMomoOrBank) {
      setAccountName(inputtedAccountName);
      finalAccountName = inputtedAccountName;      
    }
    const payload = {
      location: selectedTransferLocation,
      method: selectedOption?.title,
      fundingSource,
      transactionDescription,
      walletBalance: fundingSource?.balance,
      transfer_type: selectedOption?.id === 'cardplot' ? 'cardplot' : selectedOption?.id,
      userSelectedBank,
      transferPurpose: selectedCountry === 'CN' ? transferPurpose : undefined,
      transferRecipientType: selectedCountry === 'CN' ? transferRecipientType : null,
      transferRecipientRelationship: selectedCountry === 'CN' ? transferRecipientRelationship : null,
      invoice_document: selectedCountry === 'CN' ? invoiceDocument : undefined,
      invoice_document_file: selectedCountry === 'CN' ? invoiceDocumentFile : undefined,
      invoiceDocumentName: selectedCountry === 'CN' ? invoiceDocumentName : undefined,
      transferDocument: selectedCountry === 'CN' ? invoiceDocument : undefined,
      transferDocumentName: selectedCountry === 'CN' ? invoiceDocumentName : undefined,
      currency_to_send: selectedCountry === 'CN' ? currencyToSend : undefined,
      recipient: {
        username: selectedOption?.id === 'cardplot' ? finalRecipientName : (finalAccountName || accountName),
        bank: bankName,
        bank_uuid,
        acct_number: selectedOption?.id === 'cardplot' ? cardplotAcctNum : accountNumber,
        accountName: accountName,
        recipientCountry: selectedCountry,
        recipientCurrencyCode: selectedCountry === 'CN' ? currencyToSend : getCurrencyInfoByCountryCode(selectedCountry)?.currencyCode,
        recipient_id,
        phone,
        mobileMoney,
        userRecipientInput,
        accountNumber,
        // cardplotCurrencyId,
        // currencyRefId
      },
    };

    navigate("/user/send_money/step2", { state: { payload: payload, converterSendingDetails: converterCalDetails } });
  };

  const fetchBanks = async (currencyCode, transferMethod) => {
    if(!selectedOption) return;
    setLoadingBanks(true);
    setBanks([]); 
    try {
        const response = await fetchSupportedCountryBanks(currencyCode, transferMethod);
        const retrievedBanks = response?.data?.banks ?? [];
        if(retrievedBanks.length > 0) {
          setBanks(retrievedBanks.filter(bank => bank.iso2_country_code === selectedCountry));
        }else{
          setBankErrorMessage(`${currencyCode} ${selectedOption.title.toLowerCase()} transfers is currently NOT supported at the moment.`);  
        }
    } catch (error) {
        console.error("Error fetching banks:", error);
        setBankErrorMessage(error.response?.data?.message || 'An error ocurred while retrieving banks');
        setBanks([]);
    } finally {
        setLoadingBanks(false);
    }
  };

  useEffect(() => {
    setBankErrorMessage(null);
    if (selectedCountry && selectedOption?.id != 'cardplot') {
      const currencyCode = selectedCountry === 'CN' 
        ? currencyToSend 
        : getCurrencyInfoByCountryCode(selectedCountry).currencyCode;
      if (currencyCode) {
        fetchBanks(currencyCode, selectedOption?.id);
      }
    }
  }, [selectedCountry, currencyToSend, selectedOption]);
  

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    const payload = location.state?.payload;
    const txnAmountDetails = location.state?.txnAmountDetails;
    if (payload) {
      setSelectedOption(
        sendOptions.find(option => option.title === payload.method) || null
      );
      setFundingSource(payload.fundingSource || '');
      setTransactionDescription(payload.transactionDescription || '');
      setUserSelectedBank(payload.userSelectedBank || '');
  
      if (payload.recipient) {
        setRecipientUsername(payload.recipient.username || '');
        setUserRecipientInput(payload.recipient.userRecipientInput || '');
        setBankName(payload.recipient.bank || '');
        setBank_uuid(payload.recipient.bank_uuid || '');
        setAccountName(payload.recipient.accountName || '');
        setInputtedAccountName(payload.recipient.username || '');
        setAccountNumber(payload.recipient.accountNumber || '');
        setPhone(payload.recipient.phone || '');
        setMobileMoney(payload.recipient.mobileMoney || '');
        handleCountrySelect(payload.recipient.recipientCountry || '');
        if (payload.recipient.recipientCountry === 'CN') {
          setCurrencyToSend(payload.currency_to_send || payload.chinaCurrency || payload.recipient.recipientCurrencyCode || 'CNY');
        }
      }
      if (payload.transferPurpose) setTransferPurpose(payload.transferPurpose);
      if (payload.transferRecipientType) setTransferRecipientType(payload.transferRecipientType);
      if (payload.transferRecipientRelationship) setTransferRecipientRelationship(payload.transferRecipientRelationship);
      if (payload.invoice_document_file) {
        setInvoiceDocumentFile(payload.invoice_document_file);
      }
      if (payload.invoice_document || payload.transferDocument) {
        setInvoiceDocument(payload.invoice_document || payload.transferDocument);
      }
      if (payload.invoiceDocumentName || payload.transferDocumentName) {
        setInvoiceDocumentName(payload.invoiceDocumentName || payload.transferDocumentName);
      }
    }
    if (txnAmountDetails) {
      setConverterCalDetails(txnAmountDetails);
    }
  }, [location.state?.payload]);

  const verifyAccount = async () => {
    const credentials = {
      account_number: accountNumber,
      bank_id: userSelectedBank.id,
    };
    // console.log(userSelectedBank);
    try {
      setVerifyAccountLoading(true);

      const response = await verifyBankAccount(credentials);
      const data = response.data;
      // console.log("Verification response:", data);

      setInputtedAccountName(data.verification.account_name);
      setAccountNameFieldDisabled(true);
    } catch (error) {
      // console.error("Account verification failed:", error);
      // toast.error("Account number could not be verified");
      setAccountNameFieldDisabled(false);
    } finally {
      setVerifyAccountLoading(false);
    }
  };

  const bankOptions = banks.map((bank) => ({
    value: bank.id,
    label: bank.name,
  }));

  useEffect(() => {
    if (accountNumber.length === 10 && userSelectedBank) {
      verifyAccount();
    }
  }, [accountNumber, userSelectedBank]);


  return (
    <section className='create__card__page send__money__page' style={{ paddingTop: "30px" }}>
      <div className="container">
        <div className="d-flex align-items-center mb-2">
            <button 
              onClick={handleGoBack} 
              className="btn btn-sm d-flex align-items-center gap-2 px-3 py-2 rounded-pill shadow-sm back-button-hover" 
              style={{ backgroundColor: '#fff', color: '#4a4a4a', border: '1px solid #eaeaea', transition: 'all 0.2s ease', fontWeight: '500' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f8f9fa'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
                <FaAngleLeft size={14} />
                <span className="small">Go back</span>
            </button>
        </div>
      </div>

      <section className='mt-3'>
        <CardsProgressSteps currentStep={1} stepLabels={["Recipient", "Amount", "Review/Pay", "Success"]} />
      </section>

      <div className='container mt-4'>
        <div className="send__money__card send__money__form">
          <div className='create_card_header'>
            <h4 className='mb-0 text-center'>Recipient Details</h4>
          </div>

          <form onSubmit={handleContinue}>
            <div className="position-relative" ref={dropdownRef}>

              <div className="mb-3">
                <label className='form-label text-muted mb-0 pxs-1'>Recipient Country/Currency</label>
                <ReactFlagsSelect 
                  selected={selectedCountry} 
                  onSelect={handleCountrySelect} 
                  customLabels={customLabels}
                  countries={filteredCountryCodes}
                  disabled={isLocalTransfer}
                />
              </div>

              {selectedCountry === 'CN' && (
                <div className="mb-3">
                  <label className='form-label text-muted mb-1 ps-1'>
                    Currency to Send <span className="text-danger">*</span>
                  </label>
                  <div className="china-currency-selector">
                    <div 
                      className={`china-currency-option ${currencyToSend === 'CNY' ? 'active' : ''}`}
                      onClick={() => handleCurrencyToSendSelect('CNY')}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="china-currency-top">
                        <div className="currency-badge">
                          <img 
                            src={getFlagUrl('CN')} 
                            alt="CNY" 
                            className="currency-flag-img" 
                          />
                          <span className="currency-code-text">CNY</span>
                        </div>
                        <div className="radio-circle">
                          <div className="radio-circle-dot" />
                        </div>
                      </div>
                      <div className="currency-name-text">Chinese Yuan (¥)</div>
                    </div>

                    <div 
                      className={`china-currency-option ${currencyToSend === 'USD' ? 'active' : ''}`}
                      onClick={() => handleCurrencyToSendSelect('USD')}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="china-currency-top">
                        <div className="currency-badge">
                          <img 
                            src={getFlagUrl('US')} 
                            alt="USD" 
                            className="currency-flag-img" 
                          />
                          <span className="currency-code-text">USD</span>
                        </div>
                        <div className="radio-circle">
                          <div className="radio-circle-dot" />
                        </div>
                      </div>
                      <div className="currency-name-text">US Dollar ($)</div>
                    </div>
                  </div>
                </div>
              )}

              <label className='form-label mb-0 ps-1'>Transfer Option</label>
              <div 
                className="form-control d-flex justify-content-between align-items-center cursor-pointer"
                onClick={() => setShowDropdown(!showDropdown)}
                style={{ cursor: 'pointer', fontSize: '14px' }}
              >
                {selectedOption ? selectedOption.title : 'Select an Option'}
                <FaChevronDown />
              </div>

              {showDropdown && (
                <> 
                  <div className="border rounded mt-1 bg-white position-absolute w-100 shadow-sm mb-3"
                    style={{ zIndex: 10 }}
                  >
                    {loadingOptions ? (
                      <div className="d-flex justify-content-center p-2">
                        <div className="spinner-border spinner-border-sm text-dark" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    ) : (
                      filteredTransferOptions.length > 0 ? (
                      filteredTransferOptions.map((option) => (
                        <div
                          key={option.id}
                          className="p-2 border-bottom small"
                          onClick={() => {
                            setSelectedOption(option);
                            setShowDropdown(false);
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center gap-3">
                            <div className="p-2 rounded-circle bg-light">{option.icon}</div>
                            <div>
                              <div className="fw-bold">{option.title}</div>
                              <small className="text-muted">{option.description}</small>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-2 border-bottom small text-center">
                        { selectedCountry ? "No options available" : "Select a country first" }
                      </div>
                    )
                    )}
                  </div>
                </>
              )}
              {bankErrorMessage && <div className="pt-1 px-1 small text-danger">
                {bankErrorMessage}
              </div>}
            </div>

            {/* Conditionally Render Inputs Based on Selection */}
            <div className='mt-3'>
              <label className='form-label mb-0 ps-1'>Source Wallet</label>
              <select className="form-select mb-3 mt-0 " aria-label="Default select example"
                value={fundingSource?.id || ""} onChange={handleWalletSelectChange} disabled={isLocalTransfer}
              >
                <option value=''>Select Source Wallet</option>
                {loading ? (
                  <option>Loading...</option>
                ) : (
                  userWallets.length > 0 ? (
                    userWallets.map((option, index) => (
                      <option key={index} value={option.id}>
                        {option.currency_code} ({option.currencyName})
                      </option>                              
                    ))
                  ) : (
                    <option>No wallet found</option>
                  )
                )}
              </select>
            </div>

            <div className="mt-3">
              {selectedOption?.id === 'cardplot' && (
                <>
                  <div className="position-relative" ref={inputRecentRef}>
                    <label className='form-label mb-0 ps-1'>Recipient Email / Username</label>
                    <div className="input-group mb-4">
                      <input type="text" className="form-control border-end-0" placeholder="e.g. millybee@gmail.com" 
                        value={previewValue || userRecipientInput} 
                        onChange={(e) => {setUserRecipientInput(e.target.value); setShowRecentDropdown(true); setPreviewValue(""); setHighlightedIndex(-1);}}
                        onFocus={() => setShowRecentDropdown(true)} onBlur={() => setTimeout(() => setShowRecentDropdown(false), 200)} 
                        autoComplete='off' onKeyDown={handleRecentKeyDown}
                      />
                      <span className="input-group-text border-start-0"><RiUserReceivedLine size={24} color='#0A95FF' /></span>
                    </div>

                    {showRecentDropdown && filteredRecipients.length > 0 && (
                      <ul className="dropdown-menu show w-100" style={{ top: "100%", position: "absolute", zIndex: 10 }}>
                        {filteredRecipients.map((recipient, index) => (
                          <li key={index} className="dropdown-item" role='button' 
                            onClick={() => handleRecentRecipientSelect(recipient)}
                            onMouseEnter={() => setHighlightedIndex(index)}
                          >
                            {recipient}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div>
                    <label className='form-label mb-0 ps-1'>Transaction Description <small className='text-muted'>(Optional)</small></label>
                    <input type="text" className="form-control mb-3" 
                      value={transactionDescription} onChange={(e) => setTransactionDescription(e.target.value)} 
                    />
                  </div>
                </>
              )}
              
              {selectedOption && selectedOption.id != 'cardplot' && !bankErrorMessage && (
                <>
                  <div>
                    <label className='ms-2 text-muted mb-3 w-100'>
                      Enter the recipient’s {selectedOption.title.toLowerCase()} details for a secure transfer.
                    </label>
                    {/* <label className='form-label text-muted mb-0 ps-1'>Select Bank Name</label> */}

                    <div className="mb-3">
                      <label className='form-label text-muted mb-0 ps-1'>
                        Select {selectedOption.title}
                      </label>
                      <Select
                        options={bankOptions}
                        isLoading={loadingBanks}
                        placeholder={`Select ${selectedOption.title}`}
                        value={bankOptions.find(bankOption => bankOption.value == bank_uuid) || null}
                        onChange={(selected) => {
                          const selectedBank = banks.find(bankOption => bankOption.id === selected?.value);
                          if (selectedBank) {
                            setBank_uuid(selectedBank.id);
                            setBankName(selectedBank.name);
                            setUserSelectedBank(selectedBank);
                          }
                        }}
                        classNamePrefix="react-select"
                        isClearable
                      />
                    </div>
                  </div>
                  <div>
                    <label className='form-label text-muted mb-0 ps-1'> 
                      {selectedOption?.accountNumberLabel}
                    </label>                  
                    <input type="text" className="form-control mb-3" placeholder="Enter account number" 
                      value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className='form-label mb-0 ps-1'>
                      {selectedOption?.accountNameLabel}
                      {/* {selectedOption?.id == 'bank' ? 'Bank Account Name' : 'MoMo Account Name'} */}
                      <small>
                        {verifyAccountLoading && (
                          <div className="spinner-grow spinner-grow-sm text-primary ms-2" role="status">
                            <span className="sr-only">Loading...</span>
                          </div>
                        )}
                      </small> 
                    </label>
                    <input type="text" className="form-control mb-3" placeholder="Enter account name"
                      value={inputtedAccountName} onChange={(e) => {
                        setInputtedAccountName(e.target.value)
                        setAccountName(e.target.value)
                      }} readOnly={accountNameFieldDisabled}
                    />
                  </div>

                  {selectedCountry === 'CN' && (
                    <>
                      <div className="mb-3">
                        <label className='form-label text-muted mb-0 ps-1'>Is recipient an individual or a business?</label>
                        <Select
                          options={[
                            { value: 'individual', label: 'Individual' },
                            { value: 'business', label: 'Business' },
                          ]}
                          value={transferRecipientType}
                          onChange={setTransferRecipientType}
                          classNamePrefix="react-select"
                          placeholder="Select type of recipient"
                          isClearable
                        />
                      </div>
                      <div className="mb-3">
                        <label className='form-label text-muted mb-0 ps-1'>What is the recipient to you?</label>
                        <Select
                          options={[
                            { value: 'SPOUSE', label: 'Spouse' },
                            { value: 'PARENTS', label: 'Parent' },
                            { value: 'SONS_AND_DAUGHTERS', label: 'Son/Daughter' },
                            { value: 'BROTHERS_AND_SISTERS', label: 'Brother/Sister' },
                            { value: 'GRANDPARENTS', label: 'Grandparent' },
                            { value: 'GRANDPARENTS_IN_LAW', label: 'Grandparent-in-law' },
                            { value: 'GRANDCHILDREN', label: 'Grandchild' },
                            { value: 'MATERNAL_GRANDCHILDREN', label: 'Maternal Grandchild' },
                            { value: 'EMPLOYEE', label: 'Employee' },
                            { value: 'VENDOR', label: 'Vendor' },
                            { value: 'SELF', label: 'My Account' },
                          ]}
                          value={transferRecipientRelationship}
                          onChange={setTransferRecipientRelationship}
                          classNamePrefix="react-select"
                          placeholder="Select recipient relationship"
                          isClearable
                        />
                      </div>
                      <div className="mb-3">
                        <label className='form-label text-muted mb-0 ps-1'>Purpose of Transfer</label>
                        <Select
                          options={transferRecipientType?.value == 'business' ? [
                            { value: 'ECOMMERCE', label: 'E-Commerce' },
                            { value: 'GOODS_PURCHASE', label: 'Goods Purchase' },
                          ] : [
                            { value: 'FAMILY_SUPPORT', label: 'Family Support' },
                            { value: 'SALARY', label: 'Salary' },
                            { value: 'TRAVEL', label: 'Travel' },
                            { value: 'INSURANCE', label: 'Insurance' },
                            { value: 'SERVICE_CHARGES', label: 'Service Charges' },
                            { value: 'PATENT_ROYALTY', label: 'Patent Royalty' },
                            { value: 'OTHER', label: 'Other' },
                          ]}
                          value={transferPurpose}
                          onChange={setTransferPurpose}
                          classNamePrefix="react-select"
                          placeholder="Select Purpose"
                          isClearable
                        />
                      </div>
                      <div className="mb-3">
                        <label className='form-label text-muted mb-0 ps-1'>Invoice Document</label>
                        <input 
                          type="file" 
                          className="form-control" 
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setInvoiceDocumentFile(file);
                              setInvoiceDocumentName(file.name);
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setInvoiceDocument(reader.result);
                              };
                              reader.readAsDataURL(file);
                            } else {
                              setInvoiceDocumentFile(null);
                              setInvoiceDocument('');
                              setInvoiceDocumentName('');
                            }
                          }}
                        />
                        {invoiceDocumentName && <small className="text-success ms-1">Uploaded: {invoiceDocumentName}</small>}
                      </div>
                    </>
                  )}

                  <div>
                    <label className='form-label mb-0 ps-1'>Transaction Description <small className='text-muted'>(Optional)</small></label>
                    <input type="text" className="form-control mb-3" 
                      value={transactionDescription} onChange={(e) => setTransactionDescription(e.target.value)}
                    />
                  </div>
                </>
              )}
            </div>

            <button className='btn btn-primary mt-3 w-100' onClick={handleContinue} disabled={loading} >
              {loading ? (
                  <div className="spinner-border spinner-border-sm text-light" role="status">
                      <span className="visually-hidden">Loading...</span>
                  </div>
              ) : 'Continue'} 
            </button>
          </form>
        </div>
      </div>
    </section>
  )
}

export default Step1SendMoney